import React, { useMemo } from 'react';
import cvData from '../../data/cv';
import { Masthead } from './Masthead';
import { Rubric } from './Rubric';
import { ExperienceEntry } from './ExperienceEntry';
import { ProfileBlock, CapabilitiesBlock, EducationBlock } from './Aside';
import { EarlierExperience, type CondensedRole } from './EarlierExperience';
import { Page } from './Page';
import { periodSortKey, condenseRole } from './helpers';
import { useMeasuredPages, type Block } from './useMeasuredPages';
// A4 content area height: 297mm − 28mm margins ≈ 1017px @96dpi, minus footer.
const PAGE_HEIGHT_PX = 968;
const CONTENT_WIDTH_PX = 680;
export function CVSheet({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    experience,
    earlierExperience,
    meta
  } = cvData;
  const S = meta.sections;
  // One-page mode shows fewer detailed roles so the whole CV fits one sheet.
  const detailedCount = compact ? 4 : meta.detailedRoleCount;
  const roles = useMemo(() => [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period)), [experience]);
  const detailed = useMemo(() => roles.slice(0, detailedCount), [roles, detailedCount]);
  const condensed: CondensedRole[] = useMemo(() => [...roles.slice(detailedCount).map(condenseRole), ...earlierExperience], [roles, detailedCount, earlierExperience]);
  const blocks: Block[] = useMemo(() => {
    const list: Block[] = [];
    const gap = compact ? 'mt-6' : 'mt-11';
    list.push({
      key: 'intro',
      node: <>
          <Masthead data-id="element-54" data-mp="3u7dbgi7u9zsv" />
          <div className={`${compact ? 'mt-6' : 'mt-10'} grid grid-cols-12 gap-x-12 gap-y-10`} data-id="element-55" data-mp="3ub11h7e2s42u">
            <div className="col-span-7" data-id="element-56" data-mp="81q7nphnic47">
              <ProfileBlock compact={compact} data-id="element-57" data-mp="81hr9nmbkoo9" />
            </div>
            <div className="col-span-5" data-id="element-58" data-mp="24utenw6if5ay">
              <CapabilitiesBlock data-id="element-59" data-mp="24w4ejkwu3sws" />
            </div>
          </div>
        </>
    });
    list.push({
      key: 'exp-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-60" data-mp="21tq4ln6q3djn">
          <Rubric index={S.experience.index} title={S.experience.title} count={`${detailed.length} recent roles`} data-id="element-61" data-mp="21sx77ctnm0e6" />
        </div>
    });
    detailed.forEach((item, i) => {
      list.push({
        key: `role-${i}`,
        node: <ExperienceEntry item={item} isLast compact={compact} data-id="element-62" data-mp="26qyru26qa7rr" />
      });
    });
    // The condensed "Earlier Experience" list is omitted in one-page mode.
    if (!compact) {
      list.push({
        key: 'earlier-head',
        keepWithNext: true,
        node: <div className={gap} data-id="element-63" data-mp="2uhvpqb1ov4k0">
            <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} data-id="element-64" data-mp="2ug18p7tjfwl7" />
          </div>
      });
      condensed.forEach((r, i) => {
        list.push({
          key: `cond-${i}`,
          node: <EarlierExperience roles={[r]} data-id="element-65" data-mp="2zhgpuoslhiyb" />
        });
      });
    }
    list.push({
      key: 'edu-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-66" data-mp="3i2mwvba6j8mn" />
    });
    list.push({
      key: 'edu',
      node: <EducationBlock data-id="element-67" data-mp="3n3alxreulfe8" />
    });
    return list;
  }, [detailed, condensed, S, compact]);
  const {
    setRef,
    pages
  } = useMeasuredPages(blocks, PAGE_HEIGHT_PX);
  const measuringLayer = <div aria-hidden="true" style={{
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: '-99999px',
    top: 0,
    width: `${CONTENT_WIDTH_PX}px`
  }} data-id="element-68" data-mp="3e232gfgzqj47">
      {blocks.map(b => <div key={b.key} ref={setRef(b.key)} className="mb-5" data-id="element-69" data-mp="2qdtjdlb5w9gr">
          {b.node}
        </div>)}
    </div>;
  if (!pages) {
    return <div className="relative flex flex-col items-center" data-id="element-70" data-mp="2ufojimhcwgsy">
        {measuringLayer}
        <div className="relative bg-[var(--paper)] text-[var(--ink)] shadow-[0_8px_40px_rgba(26,24,21,0.14)]" style={{
        width: '210mm',
        padding: '14mm 15mm'
      }} data-id="element-71" data-mp="2ggf6twsd04tq">
          <div className={compact ? 'space-y-3' : 'space-y-5'} data-id="element-72" data-mp="3nr3csaqtl6za">
            {blocks.map(b => <div key={b.key} data-id="element-73" data-mp="3jr2z4sbjozta">{b.node}</div>)}
          </div>
        </div>
      </div>;
  }
  const total = pages.length;
  return <div className="relative flex flex-col items-center gap-8" data-id="element-74" data-mp="3a0zxk3qcydjc">
      {measuringLayer}
      {pages.map((pageBlocks, pageIdx) => <Page key={pageIdx} index={pageIdx + 1} total={total} data-id="element-75" data-mp="3526iw7zc4ghl" data-mpi={pageIdx}>
          <div className={compact ? 'space-y-3' : 'space-y-5'} data-id="element-76" data-mp="301smjn73iuze">
            {pageBlocks.map(b => <div key={b.key} data-id="element-77" data-mp="2w071hekerae4">{b.node}</div>)}
          </div>
        </Page>)}
    </div>;
}